import { useEffect, useState } from "react";
import { Cloud, CloudOff } from "lucide-react";
import { storage, SAVED_EVENT, SAVE_ERROR_EVENT } from "../lib/storage";
import { formatRelativeTime } from "../lib/progress";

/**
 * Tiny header badge that gives teachers confidence their changes have been
 * committed. Renders:
 *   • Nothing on first load (no writes have happened this session).
 *   • "Saved · just now" / "Saved · 2m ago" after each successful write.
 *   • A red "Save failed" pill if a write throws (quota exceeded, etc.).
 *
 * Refreshes its relative-time label once a minute so it doesn't go stale.
 */
export default function LastSavedBadge() {
    const [lastSaved, setLastSaved] = useState(storage.getLastSavedAt());
    const [error, setError] = useState(null);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const onSaved = (e) => {
            setLastSaved(e?.detail?.at || storage.getLastSavedAt());
            setError(null);
            setNow(new Date());
        };
        const onError = (e) => {
            setError(e?.detail?.error || "Unknown");
        };
        window.addEventListener(SAVED_EVENT, onSaved);
        window.addEventListener(SAVE_ERROR_EVENT, onError);
        return () => {
            window.removeEventListener(SAVED_EVENT, onSaved);
            window.removeEventListener(SAVE_ERROR_EVENT, onError);
        };
    }, []);

    // Refresh the relative-time label every 30s while mounted.
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    if (error) {
        return (
            <span
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-danger bg-danger-soft border border-danger-soft px-2 py-1 rounded-full"
                title={`Could not save: ${error}`}
                data-testid="save-error-badge"
            >
                <CloudOff size={11} strokeWidth={3} />
                Save failed
            </span>
        );
    }
    if (!lastSaved) return null;
    return (
        <span
            className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 bg-mist/50 border border-mist px-2 py-1 rounded-full whitespace-nowrap"
            title={new Date(lastSaved).toLocaleString()}
            data-testid="last-saved-badge"
        >
            <Cloud size={11} strokeWidth={3} className="text-success" />
            Saved · {formatRelativeTime(lastSaved, now)}
        </span>
    );
}
